/**
 * 認証ドメイン Zod スキーマ
 */

import { z } from "zod";
import type { User } from "./types";
import { createUserId } from "./types";

// ============================================
// スキーマ定義
// ============================================

/** 認証プロバイダースキーマ */
export const AuthProviderSchema = z.enum(["google", "email"]);

/** メール変更ステータススキーマ */
export const EmailChangeStatusSchema = z.enum([
  "pending_current_verification",
  "pending_new_verification",
  "pending_cooldown",
  "completed",
  "cancelled",
]);

/** メール変更リクエストスキーマ（KV保存データの検証用） */
export const EmailChangeRequestSchema = z.object({
  id: z.string().min(1),
  userId: z.string().min(1),
  currentEmail: z.string().email(),
  newEmail: z.string(),
  status: EmailChangeStatusSchema,
  createdAt: z.string().datetime(),
  currentVerifiedAt: z.string().datetime().optional(),
  newVerifiedAt: z.string().datetime().optional(),
  scheduledAt: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional(),
  cancelledAt: z.string().datetime().optional(),
});

/** ユーザースキーマ（KV保存データの検証用） */
export const UserSchema = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  name: z.string().min(1),
  picture: z.string().url().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  isPremium: z.boolean(),
  isAdmin: z.boolean().optional(),
  stripeCustomerId: z.string().optional(),
  purchasedAt: z.string().datetime().optional(),
  providers: z.array(AuthProviderSchema).optional(),
  googleId: z.string().optional(),
});

// ============================================
// パーサー関数
// ============================================

/**
 * JSON文字列からUserをパース（ランタイム検証付き）
 *
 * @throws {Error} JSONパースまたはスキーマ検証に失敗した場合
 */
export function parseUser(json: string): User {
  const data = JSON.parse(json);
  const result = UserSchema.safeParse(data);

  if (!result.success) {
    throw new Error(`Invalid user data: ${result.error.message}`);
  }

  return {
    ...result.data,
    id: createUserId(result.data.id),
  } as User;
}

/**
 * JSON文字列からUserをパース（失敗時はnullを返す）
 */
export function parseUserSafe(json: string): User | null {
  try {
    return parseUser(json);
  } catch {
    return null;
  }
}
